import type { ScenarioDefinition } from '../../core/types';
import { TILE_WORLD_SCALE } from '../../core/constants';
import { assetUrl } from '../../../lib/assets';

function gp(gx: number, gz: number): [number, number] { return [gx * TILE_WORLD_SCALE, gz * TILE_WORLD_SCALE]; }

const INCIDENT_POINT = gp(18, 19);
const TANKER_1_ARRIVAL = gp(15, 19);
const TANKER_2_ARRIVAL = gp(21, 19);
const VAN_ARRIVAL = gp(18, 23);
const LORRY_ARRIVAL = gp(18, 15);
const DEPOT_WEST = gp(-26, 6);
const DEPOT_EAST = gp(14, -16);
const DEPOT_SOUTH = gp(0, 30);

export const burstMainScenario: ScenarioDefinition = {
  id: 'burst-main',
  title: 'Burst Main Response',
  description: 'Storm triggers pressure sensor → AI confirms burst → tanker dispatch → crew response → isolation → resolution',
  hudLabel: 'LIVE INCIDENT',
  telemetry: 'water-network',
  activeInfraIds: ['pump-alpha', 'depot-west', 'depot-east'],
  steps: [
    // ═══════════════════════════════════════════════════════════════
    // ACT 1: THE STORM — Establishing shots (0-15s)
    // ═══════════════════════════════════════════════════════════════

    // Wide establishing shot — storm rolling in over the city center
    { type: 'parallel', duration: 7000, params: { steps: [
      { type: 'weather', duration: 3000, params: { type: 'storm' } },
      { type: 'hud-weather', duration: 0, params: { windSpeed: 45, rainfall: 28, pressure: 998 } },
      { type: 'camera', duration: 7000, params: { center: gp(0, 0), zoom: 14.5, pitch: 45, bearing: -40 } },
    ]}},

    // Slow dramatic sweep across the city center + storm ripple
    { type: 'parallel', duration: 8000, params: { steps: [
      { type: 'camera', duration: 8000, params: { center: gp(0, 0), zoom: 14.2, pitch: 55, bearing: 20 } },
      { type: 'hud-event', duration: 0, params: { icon: 'storm', title: 'STORM WARNING', detail: 'Met Office amber warning active. Wind gusts 45mph. Heavy rainfall expected.', severity: 'warning' } },
      { type: 'storm-ripple', duration: 8000, params: {} },
    ]}},

    // PRODUCT SCREEN: Predictive Prevention
    { type: 'show-screen', duration: 5000, params: {
      image: assetUrl('/screens/predictive-prevention.png'),
      device: 'desktop',
      title: 'Predictive Prevention',
      subtitle: 'AI-powered 7-day weather forecasting with risk modeling',
      role: 'Network Planner',
      url: 'app.skewb.io/intensive-care/predictive-risks',
    }},

    // ═══════════════════════════════════════════════════════════════
    // ACT 2: DETECTION — Drone orbit on pumping station (15-35s)
    // ═══════════════════════════════════════════════════════════════

    // Dim surroundings
    { type: 'dim-start', duration: 0, params: {} },

    // Dramatic zoom into pumping station — approaching from high angle
    { type: 'parallel', duration: 5000, params: { steps: [
      { type: 'camera', duration: 5000, params: { center: INCIDENT_POINT, infraId: 'pump-alpha', zoom: 17.5, pitch: 70, bearing: -30 } },
      { type: 'activate-sensor', duration: 0, params: { coordinates: INCIDENT_POINT, infraId: 'pump-alpha', color: '#ef4444', label: 'Sensor P-2847' } },
      { type: 'hud-event', duration: 0, params: { icon: 'sensor', title: 'PRESSURE DROP', detail: 'Sensor P-2847 Whitworth St. Pressure dropped -0.8 bar in 30 seconds.', severity: 'critical' } },
    ]}},

    // Drone orbit around pumping station — full 180° sweep
    { type: 'parallel', duration: 8000, params: { steps: [
      { type: 'camera-orbit', duration: 8000, params: { center: INCIDENT_POINT, infraId: 'pump-alpha', zoom: 18, pitch: 72, startBearing: -30, endBearing: 150 } },
      { type: 'show-panel', duration: 0, params: {
        id: 'sensor-panel',
        coordinates: INCIDENT_POINT,
        infraId: 'pump-alpha',
        title: 'Sensor P-2847',
        panelType: 'sensor',
        data: [
          { label: 'Pressure', value: '-0.8 bar', status: 'critical' },
          { label: 'Rate of Change', value: '-0.03/s', status: 'critical' },
          { label: 'Threshold', value: 'BREACHED', status: 'critical' },
          { label: 'Duration', value: '30 sec' },
        ]
      }},
    ]}},

    // AI confirms — hold close on station
    { type: 'hud-region', duration: 0, params: { regions: [
      { name: 'Whitworth St West', status: 'critical', properties: 42 },
      { name: 'Princess St', status: 'at-risk', properties: 89 },
      { name: 'Oxford Road', status: 'monitoring', properties: 156 },
    ]}},
    { type: 'parallel', duration: 4000, params: { steps: [
      { type: 'notification', duration: 4000, params: { title: 'AI CONTROL ROOM', message: 'Burst confirmed. 300mm cast iron main. AI confidence: 94%.', severity: 'critical' } },
      { type: 'hud-event', duration: 0, params: { icon: 'alert', title: 'BURST CONFIRMED', detail: 'AI analysis: 94% probability mains burst. 300mm CI main, installed 1968.', severity: 'critical' } },
    ]}},

    // PRODUCT SCREEN: AI Control Room
    { type: 'show-screen', duration: 6000, params: {
      image: assetUrl('/screens/control-room-pollution.png'),
      device: 'desktop',
      title: 'Intelligent Control Room',
      subtitle: 'AI detects pressure anomaly and confirms burst',
      role: 'Control Room Operator',
      url: 'app.skewb.io/control-room/pollution-storm',
    }},

    // PRODUCT SCREEN: AI Playbook
    { type: 'show-screen', duration: 6000, params: {
      image: assetUrl('/screens/ai-playbook-response.png'),
      device: 'desktop',
      title: 'AI Response Playbook',
      subtitle: '94% confidence — Emergency Containment Protocol activated',
      role: 'Incident Manager',
      url: 'app.skewb.io/response/playbook/RISK-2214142',
    }},

    { type: 'remove-panel', duration: 300, params: { id: 'sensor-panel' } },

    // ═══════════════════════════════════════════════════════════════
    // ACT 3: DISPATCH — Drone on depot, vehicle launch (35-55s)
    // ═══════════════════════════════════════════════════════════════

    // Cinematic sweep to depot west — wide establishing
    { type: 'parallel', duration: 5000, params: { steps: [
      { type: 'camera', duration: 5000, params: { center: DEPOT_WEST, infraId: 'depot-west', zoom: 16, pitch: 55, bearing: -50 } },
      { type: 'hud-event', duration: 0, params: { icon: 'dispatch', title: 'TANKER DISPATCHED', detail: 'Tanker T-104 dispatched from Operations Depot West. Capacity: 12,000L.', severity: 'info' } },
      { type: 'hud-team', duration: 0, params: { teams: [
        { name: 'Tanker T-104', status: 'dispatched', eta: '8 min', destination: 'Pumping Station Alpha' },
        { name: 'Crew Van NV-12', status: 'standby', destination: 'Depot East' },
      ]}},
    ]}},

    // Drone orbit on depot — watching tanker prepare
    { type: 'camera-orbit', duration: 6000, params: { center: DEPOT_WEST, infraId: 'depot-west', zoom: 17.5, pitch: 68, startBearing: -50, endBearing: 30 } },

    // Spawn tanker and immediately start following
    { type: 'spawn-vehicle', duration: 30000, params: { vehicleId: 'scenario-tanker-1', modelId: 'simpoly-tanker', origin: DEPOT_WEST, originInfraId: 'depot-west', destination: TANKER_1_ARRIVAL, label: 'T-104' } },

    // PRODUCT SCREEN: Driver mobile dispatch
    { type: 'show-screen', duration: 5000, params: {
      component: 'mobile-notification',
      device: 'mobile',
      title: 'Driver Mobile App',
      subtitle: 'Tanker driver receives priority dispatch notification',
      role: 'Tanker Driver',
    }},


    // ═══════════════════════════════════════════════════════════════
    // ACT 4: THE CHASE — Cinematic vehicle tracking (55-90s)
    // ═══════════════════════════════════════════════════════════════

    // Close-up low angle — as if riding under/behind the truck
    { type: 'parallel', duration: 7000, params: { steps: [
      { type: 'camera-follow', duration: 7000, params: { vehicleId: 'scenario-tanker-1', pitch: 82, zoom: 19 } },
      { type: 'show-panel', duration: 0, params: {
        id: 'tanker-panel',
        coordinates: TANKER_1_ARRIVAL,
        title: 'Tanker T-104',
        panelType: 'vehicle',
        data: [
          { label: 'Status', value: 'EN ROUTE', status: 'warning' },
          { label: 'Load', value: '12,000 L' },
          { label: 'Speed', value: '35 km/h' },
          { label: 'ETA', value: '4 min', status: 'warning' },
        ]
      }},
      { type: 'hud-team', duration: 0, params: { teams: [
        { name: 'Tanker T-104', status: 'en-route', eta: '4 min', destination: 'Pumping Station Alpha' },
        { name: 'Tanker T-207', status: 'dispatched', eta: '7 min', destination: 'Pumping Station Alpha' },
        { name: 'Crew Van NV-12', status: 'dispatched', eta: '6 min', destination: 'Pumping Station Alpha' },
        { name: 'Grab Lorry GL-03', status: 'standby', destination: 'Depot South' },
      ]}},
    ]}},

    // Side angle — cinematic tracking shot
    { type: 'camera-follow', duration: 5000, params: { vehicleId: 'scenario-tanker-1', pitch: 70, zoom: 18.5 } },

    // Pull up to higher angle — context visible
    { type: 'camera-follow', duration: 5000, params: { vehicleId: 'scenario-tanker-1', pitch: 55, zoom: 17 } },

    { type: 'remove-panel', duration: 300, params: { id: 'tanker-panel' } },

    // Cut to wide — spawn second tanker and van
    { type: 'parallel', duration: 5000, params: { steps: [
      { type: 'camera', duration: 5000, params: { center: INCIDENT_POINT, zoom: 15.5, pitch: 50, bearing: 30 } },
      { type: 'spawn-vehicle', duration: 25000, params: { vehicleId: 'scenario-tanker-2', modelId: 'simpoly-tanker2', origin: DEPOT_SOUTH, destination: TANKER_2_ARRIVAL, label: 'T-207' } },
      { type: 'spawn-vehicle', duration: 22000, params: { vehicleId: 'scenario-van', modelId: 'simpoly-pickup', origin: DEPOT_EAST, destination: VAN_ARRIVAL, label: 'NV-12' } },
      { type: 'hud-team', duration: 0, params: { teams: [
        { name: 'Tanker T-104', status: 'arriving', destination: 'Pumping Station Alpha' },
        { name: 'Tanker T-207', status: 'en-route', eta: '5 min', destination: 'Pumping Station Alpha' },
        { name: 'Crew Van NV-12', status: 'en-route', eta: '4 min', destination: 'Pumping Station Alpha' },
        { name: 'Grab Lorry GL-03', status: 'dispatched', eta: '8 min', destination: 'Pumping Station Alpha' },
      ]}},
      { type: 'hud-event', duration: 0, params: { icon: 'dispatch', title: 'REINFORCEMENTS', detail: 'T-207 and NV-12 dispatched. GL-03 on standby.', severity: 'info' } },
    ]}},

    // Tanker 2 — low close-up chase shot
    { type: 'parallel', duration: 7000, params: { steps: [
      { type: 'camera-follow', duration: 7000, params: { vehicleId: 'scenario-tanker-2', pitch: 80, zoom: 19 } },
      { type: 'show-panel', duration: 0, params: {
        id: 'tanker2-panel',
        coordinates: TANKER_2_ARRIVAL,
        title: 'Tanker T-207',
        panelType: 'vehicle',
        data: [
          { label: 'Status', value: 'EN ROUTE', status: 'warning' },
          { label: 'Load', value: '10,000 L' },
          { label: 'Speed', value: '38 km/h' },
          { label: 'ETA', value: '3 min', status: 'warning' },
        ]
      }},
    ]}},

    // Side angle on tanker 2
    { type: 'camera-follow', duration: 4000, params: { vehicleId: 'scenario-tanker-2', pitch: 68, zoom: 18.5 } },

    { type: 'remove-panel', duration: 300, params: { id: 'tanker2-panel' } },

    // Crew van — quick urgent cut, very low angle
    { type: 'parallel', duration: 6000, params: { steps: [
      { type: 'camera-follow', duration: 6000, params: { vehicleId: 'scenario-van', pitch: 78, zoom: 19 } },
      { type: 'hud-event', duration: 0, params: { icon: 'crew', title: 'CREW VAN EN ROUTE', detail: 'NV-12 (3 operatives, isolation kit) approaching from Depot East.', severity: 'info' } },
      { type: 'show-panel', duration: 0, params: {
        id: 'van-panel',
        coordinates: VAN_ARRIVAL,
        title: 'Crew Van NV-12',
        panelType: 'vehicle',
        data: [
          { label: 'Status', value: 'EN ROUTE', status: 'warning' },
          { label: 'Crew', value: '3 operatives' },
          { label: 'Kit', value: 'Isolation' },
          { label: 'ETA', value: '2 min', status: 'warning' },
        ]
      }},
    ]}},
    { type: 'remove-panel', duration: 300, params: { id: 'van-panel' } },

    // ═══════════════════════════════════════════════════════════════
    // ACT 5: ON SCENE — Resolution with orbit (90-120s)
    // ═══════════════════════════════════════════════════════════════

    { type: 'spawn-vehicle', duration: 28000, params: { vehicleId: 'scenario-lorry', modelId: 'simpoly-dump', origin: DEPOT_SOUTH, destination: LORRY_ARRIVAL, label: 'GL-03' } },

    // Overhead establishing shot — all vehicles converging
    { type: 'parallel', duration: 6000, params: { steps: [
      { type: 'camera', duration: 6000, params: { center: INCIDENT_POINT, infraId: 'pump-alpha', zoom: 17, pitch: 55, bearing: -20 } },
      { type: 'hud-team', duration: 0, params: { teams: [
        { name: 'Tanker T-104', status: 'on-site', destination: 'Pumping Station Alpha' },
        { name: 'Tanker T-207', status: 'on-site', destination: 'Pumping Station Alpha' },
        { name: 'Crew Van NV-12', status: 'on-site', destination: 'Pumping Station Alpha' },
        { name: 'Grab Lorry GL-03', status: 'en-route', eta: '3 min', destination: 'Pumping Station Alpha' },
      ]}},
      { type: 'model-status', duration: 6000, params: { coordinates: INCIDENT_POINT, infraId: 'pump-alpha', color: '#ef4444', emissive: 2.0 } },
      { type: 'hud-event', duration: 0, params: { icon: 'crew', title: 'CREWS ON SITE', detail: 'T-104, T-207, NV-12 on site. Commencing valve isolation.', severity: 'info' } },
      { type: 'show-panel', duration: 0, params: {
        id: 'pump-panel',
        coordinates: INCIDENT_POINT,
        infraId: 'pump-alpha',
        title: 'Pumping Station Alpha',
        panelType: 'infrastructure',
        data: [
          { label: 'Pressure', value: '0.2 bar', status: 'critical' },
          { label: 'Flow Rate', value: '12 L/s', status: 'critical' },
          { label: 'Status', value: 'CRITICAL', status: 'critical' },
          { label: 'Valve V-4420', value: 'OPEN', status: 'warning' },
        ]
      }},
    ]}},

    // Orbit around pumping station — RED → AMBER transition
    { type: 'parallel', duration: 7000, params: { steps: [
      { type: 'camera-orbit', duration: 7000, params: { center: INCIDENT_POINT, infraId: 'pump-alpha', zoom: 17.5, pitch: 65, startBearing: -20, endBearing: 60 } },
      { type: 'model-status', duration: 7000, params: { coordinates: INCIDENT_POINT, infraId: 'pump-alpha', color: '#f59e0b', emissive: 1.5 } },
      { type: 'hud-event', duration: 0, params: { icon: 'alert', title: 'ISOLATION IN PROGRESS', detail: 'Valve V-4420 closing. Flow rate reducing. Monitoring pressure.', severity: 'warning' } },
      { type: 'update-panel', duration: 0, params: {
        id: 'pump-panel',
        data: [
          { label: 'Pressure', value: '0.8 bar', status: 'warning' },
          { label: 'Flow Rate', value: '5 L/s', status: 'warning' },
          { label: 'Status', value: 'ISOLATING', status: 'warning' },
          { label: 'Valve V-4420', value: 'CLOSING...', status: 'warning' },
        ]
      }},
    ]}},

    // Continue orbit — AMBER → GREEN. Victory.
    { type: 'parallel', duration: 7000, params: { steps: [
      { type: 'camera-orbit', duration: 7000, params: { center: INCIDENT_POINT, infraId: 'pump-alpha', zoom: 17, pitch: 60, startBearing: 60, endBearing: 180 } },
      { type: 'model-status', duration: 7000, params: { coordinates: INCIDENT_POINT, infraId: 'pump-alpha', color: '#10b981', emissive: 1.0 } },
      { type: 'notification', duration: 5000, params: { title: 'ISOLATION COMPLETE', message: 'Valve V-4420 closed. Supply maintained via bypass route. Zero customer impact.', severity: 'success' } },
      { type: 'hud-event', duration: 0, params: { icon: 'resolve', title: 'BURST ISOLATED', detail: 'Valve closed. Supply maintained via bypass. 0 customers affected.', severity: 'success' } },
      { type: 'update-panel', duration: 0, params: {
        id: 'pump-panel',
        data: [
          { label: 'Pressure', value: '2.1 bar', status: 'good' },
          { label: 'Flow Rate', value: '0 L/s', status: 'good' },
          { label: 'Status', value: 'ISOLATED', status: 'good' },
          { label: 'Valve V-4420', value: 'CLOSED', status: 'good' },
        ]
      }},
    ]}},

    // Restore full colors
    { type: 'dim-end', duration: 0, params: {} },

    // ═══════════════════════════════════════════════════════════════
    // ACT 6: RESOLUTION & PULLBACK (120-140s)
    // ═══════════════════════════════════════════════════════════════

    // Weather clearing
    { type: 'parallel', duration: 5000, params: { steps: [
      { type: 'weather', duration: 3000, params: { type: 'clear' } },
      { type: 'hud-event', duration: 0, params: { icon: 'resolve', title: 'RESOLVED', detail: 'Incident contained. Permanent repair scheduled 06:00 tomorrow.', severity: 'success' } },
    ]}},

    // Final notification
    { type: 'parallel', duration: 4000, params: { steps: [
      { type: 'notification', duration: 4000, params: { title: 'INCIDENT RESOLVED', message: '142 customers notified. Repair crew scheduled 06:00. Zero supply interruption.', severity: 'success' } },
      { type: 'hud-event', duration: 0, params: { icon: 'resolve', title: 'COMMS SENT', detail: '142 customers notified via SMS. Repair scheduled 06:00. Zero interruption.', severity: 'success' } },
    ]}},

    { type: 'remove-panel', duration: 300, params: { id: 'pump-panel' } },

    // Majestic slow pullback to city overview
    { type: 'camera', duration: 12000, params: { center: gp(0, 0), zoom: 14, pitch: 50, bearing: -17 } },

    // Hold on overview
    { type: 'wait', duration: 4000 },
  ],
};
